import { Button, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

const SUPPLIERS_ROUTE = "/app/shops/$shopId/suppliers" as const;

export type ShopSuppliersListSearch = {
  offset: number;
  limit: number;
  search?: string;
  activeFilter: "all" | "active" | "inactive";
};

const STATUS_OPTIONS: {
  value: ShopSuppliersListSearch["activeFilter"];
  label: string;
}[] = [
  { value: "all", label: "ທຸກສະຖານະ" },
  { value: "active", label: "ເປີດໃຊ້ງານ" },
  { value: "inactive", label: "ປິດໃຊ້ງານ" },
];

export function ShopSuppliersFilter() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const nav = useNavigate({ from: SUPPLIERS_ROUTE });
  const search = (useSearch({
    from: SUPPLIERS_ROUTE,
  }) ?? {}) as ShopSuppliersListSearch;

  const [qInput, setQInput] = useState(search.search ?? "");

  useEffect(() => {
    setQInput(search.search ?? "");
  }, [search.search]);

  const debounced = useDebounceCallback((val: string) => {
    if (!shopId) return;
    nav({
      to: SUPPLIERS_ROUTE,
      params: { shopId },
      search: {
        ...search,
        offset: 0,
        search: val.trim() !== "" ? val.trim() : undefined,
      },
    });
  }, 400);

  const go = (next: Partial<ShopSuppliersListSearch>) => {
    if (!shopId) return;
    nav({
      to: SUPPLIERS_ROUTE,
      params: { shopId },
      search: {
        ...search,
        offset: 0,
        ...next,
      },
    });
  };

  const hasFilter =
    (search.search != null && search.search.trim() !== "") ||
    search.activeFilter !== "all";

  if (!shopId) return null;

  return (
    <div className="mb-2 flex flex-col gap-2 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            placeholder="ຄົ້ນຫາ (ຊື່, ໂທ, ລະຫັດ)…"
            value={qInput}
            onChange={(e) => {
              const v = e.target.value;
              setQInput(v);
              debounced(v);
            }}
            className="h-8 w-full sm:max-w-xs"
          />
          <div className="flex w-full flex-col gap-1 sm:w-44">
            <span className="sr-only">ສະຖານະ</span>
            <SimpleSelect
              value={search.activeFilter}
              onValueChange={(v) => {
                go({
                  activeFilter: v as ShopSuppliersListSearch["activeFilter"],
                });
              }}
              options={STATUS_OPTIONS}
              placeholder="ສະຖານະ"
              className="h-8 w-full"
            />
          </div>
        </div>
        {hasFilter ? (
          <Button
            variant="ghost"
            size="sm"
            type="button"
            onClick={() =>
              nav({
                to: SUPPLIERS_ROUTE,
                params: { shopId },
                search: {
                  offset: 0,
                  limit: search.limit ?? 20,
                  activeFilter: "all",
                  search: undefined,
                },
              })
            }
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        ) : null}
      </div>
    </div>
  );
}
